import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import {
  CALENDAR_ROLES,
  CalendarRole,
  DEFAULT_CALENDAR_ROLES,
} from '../../models/calendar-role';
import { CalendarService } from '../../services/calendar.service';

/**
 * "View by Role" multiselect for the Calendar Settings side modal.
 *
 * Hydrates from `CalendarService.getCurrentSelectedRoles()` so the modal
 * reflects whatever was applied last. Emits the in-flight selection up to
 * the side-modal — the side-modal is the one that pushes the change to the
 * service on Apply Settings (matching the foreman-selector wiring).
 */
@Component({
  selector: 'app-role-selector',
  imports: [
    NgSelectComponent,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    NgMultiLabelTemplateDirective,
  ],
  templateUrl: './role-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleSelectorComponent implements OnInit {
  private readonly calendarService = inject(CalendarService);

  @Output() rolesSelected = new EventEmitter<CalendarRole[]>();

  readonly roles: readonly CalendarRole[] = CALENDAR_ROLES;
  readonly formControl = new FormControl<CalendarRole[]>([], { nonNullable: true });

  constructor() {
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((selected) => {
        this.rolesSelected.emit(selected ?? []);
      });
  }

  ngOnInit(): void {
    const applied = this.calendarService.getCurrentSelectedRoles();
    this.formControl.patchValue([...applied], { emitEvent: false });
  }

  /** Restore the Foreman-only default. Used by the side-modal's Reset to Default. */
  resetSelection(): void {
    this.formControl.patchValue([...DEFAULT_CALENDAR_ROLES], { emitEvent: false });
  }
}
